// Who is signed in and which tenant they act on. A person may belong to several workspaces (/me's
// workspaces) and staff may open any customer (/me's staffTenants): the choice is kept for the browser
// session and rides on X-Tenant-Id. A person in one workspace is in it without choosing.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { api, ApiError, setApiContext } from "../api/client";
import type { Me, Role } from "../api/types";
import { useAuth } from "./AuthProvider";
import { SIGNUP_COMPANY_KEY } from "./native";
import { Splash } from "../shared/Splash";
import { CreateWorkspace } from "../screens/CreateWorkspace";
import { Button, Icon } from "../ui";

interface WorkspaceResult { tenantId: string | null; joined: boolean; created: boolean; name?: string }

const TENANT_KEY = "av-tenant";
const ROLE_ORDER: Role[] = ["Viewer", "Approver", "Admin", "Staff"];

/** Keeps (or forgets) the workspace this browser session acts on; the next /me and every call carry it. */
export function rememberTenant(tenantId: string | null) {
  if (tenantId) sessionStorage.setItem(TENANT_KEY, tenantId);
  else sessionStorage.removeItem(TENANT_KEY);
}

export interface Session {
  me: Me;
  role: Role;
  isStaff: boolean;
  /** The tenant acted on, if any: the user's chosen (or only) workspace, or the one staff opened. */
  tenantId: string | null;
  tenant: Me["tenant"];
  /** Every workspace the person belongs to; more than one means they can switch. */
  workspaces: NonNullable<Me["workspaces"]>;
  /** Staff viewing a customer: shown as the banner. */
  openedAs: boolean;
  openTenant: (tenantId: string | null) => void;
  atLeast: (role: Role) => boolean;
  canEdit: boolean;
  canAct: boolean;
  refresh: () => Promise<void>;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession outside SessionProvider");
  return value;
}

export function useOptionalSession(): Session | null {
  return useContext(SessionContext);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const location = useLocation();
  const [tenantId, setTenantId] = useState<string | null>(() => sessionStorage.getItem(TENANT_KEY));
  // An invitation link opened without a sign-in: the page handles itself, and there is nobody to ask /me about.
  const anonymous = auth.mode === "entra" && !auth.account;

  // The API client reads the token and the chosen tenant from here.
  useEffect(() => {
    setApiContext({
      getToken: auth.getToken,
      staffKey: auth.staffKey,
      tenantId,
      tenantHeader: auth.auth.tenantHeader,
      onUnauthorized: () => {
        if (auth.mode === "entra" && auth.account) auth.switchAccount();
      },
    });
  }, [auth, tenantId]);

  const me = useQuery({
    queryKey: ["me", tenantId],
    queryFn: () => api<Me>("/me"),
    enabled: !anonymous,
    retry: (count, error) => !(error instanceof ApiError) && count < 2,
    staleTime: 60_000,
  });

  const openTenant = useCallback((next: string | null) => {
    rememberTenant(next);
    setTenantId(next);
    queryClient.clear();
  }, [queryClient]);

  // A chosen tenant that no longer exists (staff), or one the person is no longer a member of: forget it.
  const notAMember = me.error instanceof ApiError && me.error.status === 403 && me.error.reason === "not-a-member";
  useEffect(() => {
    if (tenantId && ((me.error instanceof ApiError && me.error.status === 404) || notAMember)) openTenant(null);
  }, [me.error, notAMember, tenantId, openTenant]);

  // A Microsoft sign-in with no portal user yet: the API accepts every open invitation for that email.
  // Without one, a self-serve sign-up's workspace is created for the company it gave on the portal's sign-up
  // screen (native sign-in), or the create-workspace screen asks for it.
  const onInvite = location.pathname.startsWith("/invite/");
  const noUser = me.error instanceof ApiError && (me.error.invitationRequired || me.error.reason === "deactivated") && !onInvite;
  const workspace = useMutation({
    mutationFn: (body: { company?: string }) => api<WorkspaceResult>("/workspace", { method: "POST", body: { ...body, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }, noTenant: true }),
    onSuccess: async () => {
      sessionStorage.removeItem(SIGNUP_COMPANY_KEY);
      await queryClient.invalidateQueries({ queryKey: ["me"] });
    },
  });
  const { mutate: joinOrCreate, isIdle: notTried } = workspace;
  useEffect(() => {
    if (noUser && notTried) joinOrCreate({ company: sessionStorage.getItem(SIGNUP_COMPANY_KEY) ?? undefined });
  }, [noUser, notTried, joinOrCreate]);

  const value = useMemo<Session | null>(() => {
    if (!me.data) return null;
    const data = me.data;
    const role = data.user.role;
    const isStaff = role === "Staff";
    const atLeast = (needed: Role) => ROLE_ORDER.indexOf(role) >= ROLE_ORDER.indexOf(needed);
    return {
      me: data,
      role,
      isStaff,
      tenantId: data.tenant?.id ?? null,
      tenant: data.tenant,
      workspaces: data.workspaces ?? [],
      openedAs: isStaff && !!data.tenant,
      openTenant,
      atLeast,
      canEdit: atLeast("Admin"),
      canAct: atLeast("Approver"),
      refresh: async () => {
        await queryClient.invalidateQueries({ queryKey: ["me"] });
      },
    };
  }, [me.data, openTenant, queryClient]);

  if (anonymous) return <SessionContext.Provider value={null}>{children}</SessionContext.Provider>;
  if (me.isPending) return <Splash text="Loading your workspace…" />;

  if (me.error) {
    const error = me.error;
    if (notAMember && tenantId) return <Splash text="Switching workspace…" />;
    if (error instanceof ApiError && (error.invitationRequired || error.reason === "deactivated")) {
      if (onInvite) {
        // The invitation screen handles the accept itself; it needs no session.
        return <SessionContext.Provider value={null}>{children}</SessionContext.Provider>;
      }
      if (workspace.isIdle || workspace.isPending || workspace.isSuccess) return <Splash text="Setting up your workspace…" />;
      const refused = workspace.error instanceof ApiError ? workspace.error : null;
      if (refused?.reason === "company-required") {
        return <CreateWorkspace email={auth.account?.email ?? auth.account?.name ?? ""} busy={false} onCreate={(company) => workspace.mutate({ company })} onSwitch={auth.switchAccount} />;
      }
      if (refused?.reason === "work-email-required") {
        return (
          <Splash
            text="Use a work email"
            detail={<>You’re signed in as <b>{auth.account?.email ?? auth.account?.name}</b>. {refused.message} Sign up again with your company address, or ask a colleague to invite this one.</>}
            action={<div style={{ display: "flex", gap: 10 }}><Button variant="outline" leadingIcon={<Icon name="repeat" size={16} />} onClick={auth.switchAccount}>Switch account</Button><Button variant="ghost" leadingIcon={<Icon name="log-out" size={16} />} onClick={auth.signOut}>Sign out</Button></div>}
          />
        );
      }
      if (refused?.reason === "deactivated") {
        return <Splash tone="error" text="This account is deactivated" detail={refused.message} action={<Button variant="outline" onClick={auth.signOut}>Sign out</Button>} />;
      }
      if (refused && refused.status !== 404) {
        return (
          <Splash tone="error" text="Couldn’t set up your workspace" detail={refused.message}
            action={<div style={{ display: "flex", gap: 10 }}><Button variant="outline" onClick={() => workspace.reset()}>Try again</Button><Button variant="ghost" leadingIcon={<Icon name="log-out" size={16} />} onClick={auth.signOut}>Sign out</Button></div>} />
        );
      }
      return (
        <Splash
          text="You’re not in a workspace yet"
          detail={<>You’re signed in as <b>{auth.account?.email ?? auth.account?.name}</b>, but no AutoVouch workspace has invited that account. Open the invitation link you were sent, or ask your administrator to invite you.</>}
          action={<div style={{ display: "flex", gap: 10 }}><Button variant="outline" leadingIcon={<Icon name="repeat" size={16} />} onClick={auth.switchAccount}>Switch account</Button><Button variant="ghost" leadingIcon={<Icon name="log-out" size={16} />} onClick={auth.signOut}>Sign out</Button></div>}
        />
      );
    }
    if (error instanceof ApiError && error.status === 403) {
      return <Splash tone="error" text="This account is deactivated" detail={error.problem.title} action={<Button variant="outline" onClick={auth.signOut}>Sign out</Button>} />;
    }
    return <Splash tone="error" text="AutoVouch couldn’t load your workspace" detail={error instanceof Error ? error.message : String(error)} action={<div style={{ display: "flex", gap: 10 }}><Button variant="outline" onClick={() => void me.refetch()}>Try again</Button><Button variant="ghost" onClick={auth.signOut}>Sign out</Button></div>} />;
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
